<script setup lang="ts">
import { onMounted } from "vue";
import AppImage from "../../components/AppImage.vue";
import SiteHeader from "../../components/SiteHeader.vue";
import FooterLower from "../../components/FooterLower.vue";
import FooterModal from "../../components/FooterModal.vue";
import { useCommon } from "../../composables/useCommon";

const common = useCommon();

onMounted(() => {
  document.documentElement.classList.add("is-loaded");
});

const openModal = (modalNum: number) => {
  common.isModalNum.value = modalNum;
  if (!common.isModalOpen.value) {
    common.modalToggle();
  }
};

const closeModal = () => {
  if (common.isModalOpen.value) {
    common.modalToggle();
  }
};
</script>

<template>
  <div
    class="be-wrapper"
    :class="{ 'is-menuopen': common.isMenuOpen.value }"
    v-cloak
  >
    <SiteHeader @toggle-menu="common.menuToggle" />

    <main id="be-content" class="be-content">
      <div class="be-breadcrumb">
        <div class="be-inner">
          <ul class="be-breadcrumb-list">
            <li class="be-breadcrumb-list__li"><a href="/">TOP</a></li>
            <li class="be-breadcrumb-list__li">採用情報</li>
          </ul>
        </div>
      </div>
      <div class="be-pagetitle">
        <div class="be-inner">
          <h1 class="be-pagetitle__title js-scrollfade">
            <span class="font-en"><small>○ </small>Recruit</span
            ><span class="be-pagetitle__title__sub">採用情報</span>
          </h1>
        </div>
      </div>

      <div class="be-recruit">
        <div class="be-inner be-recruit__inner">
          <div class="be-recruit-hero">
            <div class="be-recruit-hero__desc js-scrollfade">
              Bot Expressのミッションは<br />
              「日本の行政サービスをアップデートする」こと。<br />
              <br />
              行政視点から住民視点へ。<br />
              理論上みんなが使えるサービスから、<br />
              実質的にほとんどの人が使え、<br
                class="be-hidden-pc"
              />住民利用率・満足度の高いサービスへ。<br />
              <br />
              私たちは共鳴する自治体と<br
                class="be-hidden-pc"
              />超特急でコンセプトを実現し、<br />
              それを全国に展開することで<br
                class="be-hidden-pc"
              />日本をアップグレードしていきます。<br />
              簡単な挑戦ではないけれど、<br
                class="be-hidden-pc"
              />最高のチームで日々挑戦しています。
            </div>
          </div>

          <div class="be-recruit-section">
            <div class="be-recruit-culture-wrap">
              <div class="be-recruit-culture">
                <div class="be-recruit-culture-block">
                  <div class="be-recruit-culture-block__thumb js-scrollfade">
                    <AppImage
                      src="/assets/recruit/img/c_thumb_01.jpg"
                      alt="Humility, Respect, Trust"
                      width="480"
                      height="600"
                      class="be-resize"
                    />
                  </div>
                  <div class="be-recruit-culture-block__content">
                    <h2 class="be-recruit-culture-block__title js-scrollfade">
                      Humility, Respect, Trust
                    </h2>
                    <p class="be-recruit-culture-block__text js-scrollfade">
                      どの要素よりもまず前提になるのがHRT。謙虚な心構え、敬意を持つこと、信頼を重んじること、を意味します。どれだけずば抜けた能力があっても、HRTなきところに共鳴はないと考えています。
                    </p>
                  </div>
                </div>
              </div>

              <div class="be-recruit-culture">
                <a
                  href="https://note.bot-express.com/n/n105e0f7702a4"
                  target="_blank"
                  rel="noopener"
                  class="be-recruit-culture-block"
                >
                  <div class="be-recruit-culture-block__thumb js-scrollfade">
                    <AppImage
                      src="/assets/recruit/img/c_thumb_02.jpg"
                      alt="山のオフィス"
                      width="480"
                      height="600"
                      class="be-resize"
                    />
                  </div>
                  <div class="be-recruit-culture-block__content">
                    <h2 class="be-recruit-culture-block__title js-scrollfade">
                      山のオフィス
                    </h2>
                    <p class="be-recruit-culture-block__text js-scrollfade">
                      私たちが考える理想のワークライフスタイルを実現するため、長野県茅野市に位置する蓼科高原約600坪の土地にオフィスを建築しました。私たちが最も集中できる場所であり、心地良くおこなうための場所となります。
                    </p>
                    <div
                      class="be-recruit-culture-block__arrow js-scrollfade"
                    ></div>
                  </div>
                </a>
              </div>

              <div class="be-recruit-culture">
                <div class="be-recruit-culture-block">
                  <div class="be-recruit-culture-block__thumb js-scrollfade">
                    <AppImage
                      src="/assets/recruit/img/c_thumb_03.jpg"
                      alt="働く場所・時間・休暇は自分が決める"
                      width="480"
                      height="600"
                      class="be-resize"
                    />
                  </div>
                  <div class="be-recruit-culture-block__content">
                    <h2 class="be-recruit-culture-block__title js-scrollfade">
                      働く場所・時間・休暇は自分が決める
                    </h2>
                    <p class="be-recruit-culture-block__text js-scrollfade">
                      環境によって自分たちのパフォーマンスや充実感は異なります。もちろん、全員が１つの環境下で同じように感じるわけではなく、ゾーンに入る条件は人それぞれ。働く場所や時間、休暇は各自が自由に選択しています。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="be-recruit-section">
            <h2 class="be-subtitle js-scrollfade" style="padding-left: 0px">
              <i style="background-color: rgb(11, 100, 70)"></i
              ><span>募集職種</span>
            </h2>

            <div style="margin-top: 20px">
              <ul>
                <li>
                  <a
                    target="_blank"
                    class="be-button--text be-bold"
                    href="https://bot-express.notion.site/Bot-Express-Sales-7aaaf13837bb4ad095762e150f557d14"
                    rel="noopener"
                    ><span>営業</span><span class="be-icon--arrow2"></span
                  ></a>
                </li>
                <li>
                  <a
                    target="_blank"
                    class="be-button--text be-bold"
                    href="https://bot-express.notion.site/Bot-Express-Partner-Success-Manager-02b6a5ec6f8d481f963f37260cb7f0cc"
                    rel="noopener"
                    ><span>パートナーサクセスマネージャー</span
                    ><span class="be-icon--arrow2"></span
                  ></a>
                </li>
              </ul>
            </div>

            <div class="be-footer__btn">
              <div style="float: left; margin-bottom: 20px">
                <a
                  href="https://bot-express.notion.site/Bot-Express-3e71e82bf7f84e0daac1d9156071fcdf"
                  target="_blank"
                  rel="noopener"
                  class="be-button--white"
                  ><span>採用特設サイト</span>
                  <span class="be-button--white__arrow"></span
                ></a>
              </div>
              <div style="float: left; width: 20px">&nbsp;</div>
              <div style="float: left">
                <a
                  href="https://form.bot-express.com/recruit"
                  target="_blank"
                  rel="noopener"
                  class="be-button--white"
                  ><span>応募・問い合わせ・カジュアル面談申込</span>
                  <span class="be-button--white__arrow"></span
                ></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <FooterLower @open-modal="openModal" />
    <FooterModal
      :is-open="common.isModalOpen.value"
      :modal-num="common.isModalNum.value"
      @close="closeModal"
    />
  </div>
</template>
